import { type CSSProperties, useEffect, useState } from "react";
import { Gift } from "lucide-react";
import { openNostiumWidget } from "@/lib/nostium";
import { apiSync } from "@/lib/api";
import {
  getFallbackNostiumBottomOffset,
  getFallbackNostiumButtonSize,
  getNostiumButton,
} from "@/lib/nostiumButton";

const GIFT_WIDGET_DROP_STORAGE_KEY = "gift-widget-last-drop-at";
const GIFT_WIDGET_DROP_INTERVAL_MS = 24 * 60 * 60 * 1000;
const GIFT_WIDGET_DROP_ANIMATION_MS = 2800;

let lastGiftWidgetDropAtMemory: number | null = null;

const readLastGiftWidgetDropAt = () => {
  if (typeof window === "undefined") return lastGiftWidgetDropAtMemory;

  try {
    const storedValue = window.localStorage.getItem(GIFT_WIDGET_DROP_STORAGE_KEY);
    const parsedValue = Number.parseInt(storedValue ?? "", 10);

    return Number.isFinite(parsedValue) ? parsedValue : lastGiftWidgetDropAtMemory;
  } catch {
    return lastGiftWidgetDropAtMemory;
  }
};

const persistLastGiftWidgetDropAt = (timestamp: number) => {
  lastGiftWidgetDropAtMemory = timestamp;

  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(GIFT_WIDGET_DROP_STORAGE_KEY, String(timestamp));
  } catch {
    // localStorage may be blocked in some browsers or embeds
  }
};

const GiftVoucherWidget = () => {
  const settings = apiSync.getSiteSettings();
  const [buttonSize, setButtonSize] = useState(40);
  const [bottomOffset, setBottomOffset] = useState(20);
  const isEnabled = settings?.giftVoucherWidgetEnabled !== false;
  const tooltipText = settings?.giftVoucherTooltipText?.trim() || "Bestel een cadeaubon!";
  const [shouldPlayDropAnimation, setShouldPlayDropAnimation] = useState(false);

  useEffect(() => {
    if (!isEnabled) {
      setShouldPlayDropAnimation(false);
      return;
    }

    if (typeof window === "undefined") return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const isDevelopment = import.meta.env.DEV;
    const now = Date.now();
    const lastDropAt = readLastGiftWidgetDropAt();
    const shouldPlay =
      isDevelopment || lastDropAt === null || now - lastDropAt >= GIFT_WIDGET_DROP_INTERVAL_MS;

    if (!shouldPlay) return;

    setShouldPlayDropAnimation(true);

    if (!isDevelopment) {
      persistLastGiftWidgetDropAt(now);
    }

    const timeoutId = window.setTimeout(() => {
      setShouldPlayDropAnimation(false);
    }, GIFT_WIDGET_DROP_ANIMATION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isEnabled]);

  useEffect(() => {
    const updateMetrics = () => {
      const nostiumButton = getNostiumButton();
      const viewportWidth = window.innerWidth;

      if (!nostiumButton) {
        setButtonSize(getFallbackNostiumButtonSize(viewportWidth));
        setBottomOffset(getFallbackNostiumBottomOffset(viewportWidth));
        return;
      }

      const rect = nostiumButton.getBoundingClientRect();
      const nostiumHeight = Math.round(rect.height);
      const nostiumBottomOffset = Math.round(window.innerHeight - rect.bottom);

      setButtonSize((nostiumHeight || getFallbackNostiumButtonSize(viewportWidth)) + 20);
      setBottomOffset(
        Number.isFinite(nostiumBottomOffset) && nostiumBottomOffset >= 0
          ? nostiumBottomOffset
          : getFallbackNostiumBottomOffset(viewportWidth),
      );
    };

    const mutationObserver = new MutationObserver(updateMetrics);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.addEventListener("resize", updateMetrics, { passive: true });
    updateMetrics();

    return () => {
      mutationObserver.disconnect();
      window.removeEventListener("resize", updateMetrics);
    };
  }, []);

  const iconSize = Math.max(16, Math.round(buttonSize * 0.45));
  const widgetStyle = {
    bottom: bottomOffset,
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
    "--gift-widget-bottom-offset": `${bottomOffset}px`,
  } as CSSProperties;

  if (!isEnabled) return null;

  return (
    <div
      className="fixed left-4 z-40 md:left-5"
      style={widgetStyle}
    >
      <div className={`group relative ${shouldPlayDropAnimation ? "gift-widget-drop-bounce" : ""}`}>
        <button
          type="button"
          onClick={openNostiumWidget}
          aria-label="Bestel een cadeaubon"
          className="gift-widget-wiggle flex items-center justify-center rounded-full border border-white/70 bg-gradient-to-br from-[#ff8ab0] via-[#f973a8] to-[#ec4899] text-white shadow-lg shadow-pink-400/40 transition-all duration-300 hover:-translate-y-0.5 hover:scale-105 hover:shadow-xl hover:shadow-pink-500/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2"
          style={{ width: buttonSize, height: buttonSize }}
        >
          <Gift size={iconSize} strokeWidth={2.1} />
        </button>

        <span className="pointer-events-none absolute left-[calc(100%+12px)] top-1/2 hidden -translate-y-1/2 whitespace-nowrap rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground opacity-0 shadow-lg transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100 group-focus-within:translate-x-0.5 group-focus-within:opacity-100 md:inline-flex">
          {tooltipText}
        </span>
      </div>
    </div>
  );
};

export default GiftVoucherWidget;
